import { useState, useCallback } from 'react';
import { invoiceService, InvoiceResponse, InvoiceGenerateRequest } from '../services/invoiceService';

export const useInvoice = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [invoiceData, setInvoiceData] = useState<InvoiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const getInvoice = useCallback(async (orderId: string) => {
    setIsLoading(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await invoiceService.getOrderInvoice(orderId);
      setInvoiceData(response);
      
      // Handle no invoice data case as informational message
      if (!response.success && response.error === 'no_invoice_data') {
        setInfoMessage(response.message);
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to get invoice information';
      console.error('Failed to get invoice:', error);
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateInvoice = useCallback(async (orderId: string, request: InvoiceGenerateRequest = {}) => {
    setIsGenerating(true);
    setError(null);
    setInfoMessage(null);
    try {
      const response = await invoiceService.generateOrderInvoice(orderId, request);
      setInvoiceData(response);
      
      // Handle no invoice data case as informational message
      if (!response.success && response.error === 'no_invoice_data') {
        setInfoMessage(response.message);
      } else if (!response.success && response.message) {
        setError(response.message);
      }
      
      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to generate invoice';
      console.error('Failed to generate invoice:', error);
      setError(errorMessage);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const downloadInvoice = useCallback(async (invoiceId: string) => {
    setError(null);
    try {
      await invoiceService.downloadInvoice(invoiceId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to download invoice';
      console.error('Failed to download invoice:', error);
      setError(errorMessage);
      throw error;
    }
  }, []);

  const getDownloadUrl = useCallback((invoiceId: string) => {
    return invoiceService.getDownloadUrl(invoiceId);
  }, []);

  const clearInvoiceData = useCallback(() => {
    setInvoiceData(null);
  }, []);

  return {
    isLoading,
    isGenerating,
    invoiceData,
    error,
    infoMessage,
    getInvoice,
    generateInvoice,
    downloadInvoice,
    getDownloadUrl,
    clearInvoiceData,
    clearError: () => setError(null),
    clearInfoMessage: () => setInfoMessage(null),
  };
};

export default useInvoice;